import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trash2, RotateCcw, History } from "lucide-react";
import { toast } from "sonner";

const fmtData = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const giorniRimasti = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

const AZIONI = {
  eliminata: { label: "Eliminata", cls: "bg-amber-100 text-amber-800" },
  ripristinata: { label: "Ripristinata", cls: "bg-emerald-100 text-emerald-800" },
  eliminata_definitivamente: { label: "Eliminata definitivamente", cls: "bg-red-100 text-red-800" },
  scaduta_cestino: { label: "Scaduta dal cestino", cls: "bg-slate-200 text-slate-700" },
};

export default function Cestino() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { data: cestino = [], isLoading } = useQuery({
    queryKey: ["cestino"],
    queryFn: async () => (await api.get("/cestino")).data,
  });
  const { data: log = [] } = useQuery({
    queryKey: ["eliminazioni-log"],
    queryFn: async () => (await api.get("/eliminazioni-log")).data,
  });

  const refresh = () => {
    qc.invalidateQueries();
  };
  const onError = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  const ripristinaMut = useMutation({
    mutationFn: async (id) => (await api.post(`/cestino/${id}/ripristina`)).data,
    onSuccess: () => { refresh(); toast.success("Elemento ripristinato"); },
    onError,
  });
  const purgeMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/cestino/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("Elemento eliminato definitivamente"); },
    onError,
  });

  return (
    <div className="space-y-8" data-testid="cestino-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-3">
          <Trash2 className="h-8 w-8 text-primary" /> Cestino
        </h1>
        <p className="text-slate-500 mt-1">
          Tutto ciò che viene eliminato resta recuperabile per 30 giorni, poi viene rimosso automaticamente.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Elementi nel cestino ({cestino.length})</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-slate-400 py-6">Caricamento…</div>
          ) : cestino.length === 0 ? (
            <div className="text-slate-400 py-6 text-center" data-testid="cestino-vuoto">Il cestino è vuoto.</div>
          ) : (
            <div className="divide-y">
              {cestino.map((c) => (
                <div key={c.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`cestino-item-${c.id}`}>
                  <Badge className="bg-slate-800 text-white text-xs" data-testid={`cestino-tipo-${c.id}`}>{c.tipo}</Badge>
                  <div className="flex-1 min-w-[220px]">
                    <div className="font-semibold text-slate-900">
                      {c.codice && <span className="font-mono text-sm text-slate-500 mr-2">{c.codice}</span>}
                      {c.descrizione}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      Eliminato il {fmtData(c.deleted_at)} da {c.deleted_by || "—"}
                      {c.n_ore != null && <span> · {c.n_ore} ore</span>}
                      {c.n_fatture > 0 && <span className="text-red-600"> · {c.n_fatture} fatture emesse</span>}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs" data-testid={`cestino-scadenza-${c.id}`}>
                    {giorniRimasti(c.scadenza)} giorni rimasti
                  </Badge>
                  <Button size="sm" onClick={() => ripristinaMut.mutate(c.id)} disabled={ripristinaMut.isPending} data-testid={`cestino-ripristina-${c.id}`}>
                    <RotateCcw className="h-4 w-4 mr-1.5" /> Ripristina
                  </Button>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => window.confirm(`Eliminare definitivamente "${c.descrizione}"? Non sarà più recuperabile.`) && purgeMut.mutate(c.id)}
                      disabled={purgeMut.isPending}
                      data-testid={`cestino-elimina-def-${c.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" /> Elimina definitivamente
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><History className="h-5 w-5" /> Registro eliminazioni commesse</CardTitle>
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <div className="text-slate-400 py-6 text-center" data-testid="log-vuoto">Nessuna eliminazione registrata.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="eliminazioni-log-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-3">Data</th>
                    <th className="py-2 pr-3">Azione</th>
                    <th className="py-2 pr-3">Commessa</th>
                    <th className="py-2 pr-3">Utente</th>
                    <th className="py-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((r) => {
                    const a = AZIONI[r.azione] || { label: r.azione, cls: "" };
                    return (
                      <tr key={r.id} className="border-b last:border-0" data-testid={`log-row-${r.id}`}>
                        <td className="py-2 pr-3 whitespace-nowrap">{fmtData(r.data)}</td>
                        <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${a.cls}`}>{a.label}</span></td>
                        <td className="py-2 pr-3"><span className="font-medium">{r.codice}</span> <span className="text-slate-500">{r.descrizione}</span></td>
                        <td className="py-2 pr-3">{r.utente_nome}{r.utente_email ? <span className="text-slate-400"> ({r.utente_email})</span> : null}</td>
                        <td className="py-2 text-slate-500">
                          {r.forzata ? `Forzata con ${r.n_fatture} fatture emesse` : r.n_fatture > 0 ? `${r.n_fatture} fatture emesse` : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
